import '../../styles/cabinet-trial.css';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Help, Modal, useToast } from '@/components/kit/ds';
import { useLingerAfterClose } from '@/components/domains/shared/useLingerAfterClose';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import { useOrgTopUp } from '@/hooks/useOrgTopUp';
import { fetchBillingThunk } from '@/store/billingSlice';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import { useStrings } from '@/i18n';
import {
  billingStrings,
  type BillingTextKey,
} from '@/i18n/dictionaries/billing';
import { localizeError } from '@/utils/localizeError';
import { reasonCodeForCreateLimit } from '@/utils/tierLimit';
import { usePlanView, type PlanView } from './planView';
import PlanTermsModal from './PlanTermsModal';
import {
  TrialPick,
  trialOfferCopy,
  useCanSettle,
  usePlanCheckout,
  usePlanTerms,
  useShowPlanTerms,
  useTrialOffer,
} from './CurrentPlanCard';

/**
 * WEB-4180 (деньги) — окно «Начните пробный период» вместо окна выбора тарифа
 * (эталон рескина c9ab75a2, `TrialAsk` в `sections/org.jsx`): тариф один,
 * человек выбирает только период — две карточки `TrialPick` со своей кнопкой
 * (общий узел с блоком «до пробного» на «Балансе и тарифе»). Суммы — только из
 * `planTerms.tariff` ответа `GET /billing`, каталог Stripe не зовётся.
 *
 * Пробный обещается только при признаке сервера `trialOffer: 'offered'` (одна
 * функция сервера с оформлением); `used_by_you` / `used_by_org` / нет
 * признака — «Оформить тариф» и кнопки «Оплатить <сумма>». Вид тарифа
 * (`usePlanView`): `none` — карточки (без права на деньги — «распоряжается
 * владелец»); `hidden` — то же без «К тарифу»; `unpaid` / `debt` / `pastdue` /
 * `unknown` — «Тариф не оплачен», без цены, «К тарифу» → `/org/balance`;
 * `active` / `trial` (лимиты при включённом `PLATFORM_TIER_LIMITS_ENABLED`) —
 * фраза причины общей лестницей `localizeError` (текст вида — открытый вопрос
 * задания).
 *
 * Причина окна — по коду отказа (`reasonCode`) таблицей `TRIAL_ASK_WHY`,
 * свойство `why="ai"` главнее кода (будущая студия). Сторож двух концов
 * `src/__tests__/pricingModalReason.twoEnds.web1908.guard.test.ts`: денежные
 * коды реестра сервера = ключи таблицы ∪ перечень «прочее», и каждая дверь
 * показа окна передаёт `reasonCode`.
 *
 * Поведение старого окна сохранено: Esc и щелчок мимо закрывают (крестика нет
 * — подвал); на время оформления окно занято (`busy`); второй щелчок во время
 * запроса не шлёт второй `POST /billing/checkout` (в том числе в гаснущем
 * окне — `useLingerAfterClose`); удалось — переход на адрес оплаты, окно не
 * закрывается до ухода страницы.
 */

export type TrialAskWhy = 'publish' | 'project' | 'limit' | 'ai';

/**
 * Код отказа → причина окна (таблица «Пример» задания WEB-4180). Коды лимита
 * создания — только через помощник `reasonCodeForCreateLimit` (сторож
 * `createLimitClientGateReason.rev26`: строкой в страницах они не пишутся).
 */
export const TRIAL_ASK_WHY: Readonly<Record<string, TrialAskWhy>> = {
  UNPAID_PUBLISH_CLOSED: 'publish',
  PAYMENT_REQUIRED_PUBLISH_ON_FREE: 'publish',
  [reasonCodeForCreateLimit('project', 'FREE')]: 'project',
  [reasonCodeForCreateLimit('project', 'PRO')]: 'project',
  UNPAID_AI_CLOSED: 'ai',
  // голый код ИИ-дверей сервера без reasonCode (третий ответ сессии 52, п. 2)
  PRO_REQUIRED: 'ai',
};

/**
 * Денежные коды отказа, которые сознательно идут в «прочее» («Это откроется с
 * пробным периодом»). Сторож WEB-1908 сверяет: каждый код `UNPAID_*` /
 * `PAYMENT_REQUIRED_*` реестра сервера — либо ключ таблицы, либо здесь.
 */
export const TRIAL_ASK_OTHER: readonly string[] = [
  reasonCodeForCreateLimit('quiz', 'FREE'),
  reasonCodeForCreateLimit('paywall', 'FREE'),
  'UNPAID_FUNNEL_LIMIT',
  'UNPAID_DOMAINS_CLOSED',
  'UNPAID_SMART_LINKS_CLOSED',
  'UNPAID_BRIDGE_CLOSED',
  'UNPAID_TOPUP_CLOSED',
  reasonCodeForCreateLimit('quiz', 'PRO'),
  reasonCodeForCreateLimit('paywall', 'PRO'),
  'PAYMENT_REQUIRED_TIER_LIMIT_FUNNELS',
  'PAYMENT_REQUIRED_TIER_LIMIT_FUNNELS_DETACH',
  'PAYMENT_REQUIRED_TIER_LIMIT_DOMAINS',
  'PAYMENT_REQUIRED_LIMIT_CHECK_INCOMPLETE',
];

const WHY_TEXT: Readonly<Record<TrialAskWhy, BillingTextKey>> = {
  publish: 'trialAskWhyPublish',
  project: 'trialAskWhyProject',
  limit: 'trialAskWhyLimit',
  ai: 'trialAskWhyAi',
};

/** Причина окна: `why` главнее кода; код без строки таблицы — «прочее». */
export function trialAskWhy(reasonCode?: string, why?: 'ai'): TrialAskWhy {
  if (why === 'ai') return 'ai';
  if (
    reasonCode &&
    Object.prototype.hasOwnProperty.call(TRIAL_ASK_WHY, reasonCode)
  ) {
    return TRIAL_ASK_WHY[reasonCode];
  }
  return 'limit';
}

/** Виды «не оплачено»: цены нет, «К тарифу» ведёт на «Баланс и тариф». */
const UNPAID_VIEWS: ReadonlySet<PlanView> = new Set<PlanView>([
  'unpaid',
  'debt',
  'pastdue',
  'unknown',
]);

export interface TrialAskProps {
  open: boolean;
  onClose: () => void;
  /** Код причины денежного отказа (`UNPAID_*`, `PAYMENT_REQUIRED_*`, `PRO_REQUIRED`). */
  reasonCode?: string;
  /** Явная причина (будущая студия); есть — код отказа не читается. */
  why?: 'ai';
  /** `trial-ask` по умолчанию, `publish-plan` в конструкторе. */
  testid?: string;
}

interface WindowProps extends TrialAskProps {
  readonly showToast: (text: string, kind: 'err') => void;
  readonly onOpenTerms: () => void;
}

function TrialAskWindow({
  open,
  onClose,
  reasonCode,
  why,
  testid,
  showToast,
  onOpenTerms,
}: WindowProps) {
  const s = useStrings(billingStrings);
  const dispatch = useAppDispatch();
  const view = usePlanView();
  const terms = usePlanTerms();
  const offer = useTrialOffer();
  const canSettle = useCanSettle();
  // «К тарифу» — через дверь «Баланса» (сторож WEB-2952): вид тарифа и право
  // судит АКТИВНАЯ организация (`usePlanView`, `useCanSettle`), поэтому дверь
  // зовётся по ней же — организация не переключается, ведёт на её «Баланс».
  const activeOrgId = useAppSelector(
    (state) => state.organizations?.activeOrgId ?? null,
  );
  const { goToBalance } = useOrgTopUp(activeOrgId);
  const showTerms = useShowPlanTerms();
  const loaded = useAppSelector(
    (state) => state.billing?.billingLoaded ?? false,
  );
  const { projectId, busy, pay } = usePlanCheckout(showToast);

  // Условий нет и обзор не загружен — один GET /billing (окно открывается и
  // вне «Баланса»: конструктор, списки).
  const asked = useRef(false);
  useEffect(() => {
    if (!open || terms !== null || loaded || asked.current) return;
    asked.current = true;
    void dispatch(fetchBillingThunk(undefined));
  }, [open, terms, loaded, dispatch]);

  const reason = s[WHY_TEXT[trialAskWhy(reasonCode, why)]] as string;

  let title: React.ReactNode = s.planNoneTitle;
  let help: string | null = null;
  let body: React.ReactNode;
  if (!loaded) {
    body = (
      <span className="bx-nt">
        <b>{reason}</b>
      </span>
    );
  } else if (view === 'hidden' || (view === 'none' && !canSettle)) {
    body = (
      <span className="bx-nt">
        <span className="hint">{s.planOwnerOnly}</span>
      </span>
    );
  } else if (view === 'none') {
    const copy = trialOfferCopy(s, offer, terms);
    title = copy.title;
    help = copy.help;
    body = (
      <>
        <span className="bx-nt">
          {offer === 'offered' ? (
            <>
              <b>{reason}</b>
              {copy.line && (
                <span className="hint" data-testid="trial-ask-early">
                  {copy.line}
                </span>
              )}
            </>
          ) : (
            <b>{copy.line ?? reason}</b>
          )}
        </span>
        {terms && (
          <TrialPick
            terms={terms}
            offer={offer}
            busy={busy}
            projectId={projectId}
            testid="trial-ask-pick"
            onStart={(period) => void pay(period)}
          />
        )}
      </>
    );
  } else if (UNPAID_VIEWS.has(view)) {
    title = s.planUnpaidTitle;
    body = (
      <>
        <span className="bx-nt">
          <b>{reason}</b>
        </span>
        <div>
          <Button
            kind="primary"
            data-testid="trial-ask-to-plan"
            onClick={() => {
              onClose();
              void goToBalance();
            }}
          >
            {s.trialAskToPlan}
          </Button>
        </div>
      </>
    );
  } else {
    // active / trial — лимиты при включённом PLATFORM_TIER_LIMITS_ENABLED
    title = s.prTitle;
    body = (
      <span className="bx-nt">
        <b>{localizeError({ reasonCode }, '')}</b>
      </span>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={520}
      testid={testid || 'trial-ask'}
      busy={busy}
      title={
        <>
          {title}
          {help && <Help tip={help} />}
        </>
      }
      footer={
        showTerms ? (
          <Button
            kind="ghost"
            data-testid="trial-ask-plans"
            disabled={busy}
            onClick={() => {
              onClose();
              onOpenTerms();
            }}
          >
            {s.prTitle}
          </Button>
        ) : undefined
      }
    >
      <div className="bx-ask">{body}</div>
    </Modal>
  );
}

export default function TrialAsk(props: TrialAskProps) {
  const theme = useAppSelector((state) => state.auth?.user?.theme);
  const root = useCabinetRootAttributes(theme);
  const mounted = useLingerAfterClose(props.open);
  const [termsOpen, setTermsOpen] = useState(false);
  const [toast, showToast] = useToast();
  if (!mounted && !termsOpen && !toast) return null;
  return (
    <div {...root}>
      {mounted && (
        <TrialAskWindow
          {...props}
          showToast={showToast}
          onOpenTerms={() => setTermsOpen(true)}
        />
      )}
      {termsOpen && <PlanTermsModal open onClose={() => setTermsOpen(false)} />}
      {toast}
    </div>
  );
}
